import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import OptimizedImage from '@/components/OptimizedImage';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { structuredDataGenerator } from '@/utils/structuredData';
import { getDomainUtils } from '@/utils/domainUtils';

// Mock blog data - in a real application this would come from an API or CMS
const blogArticles = [
  {
    id: 1,
    title: 'Getting Started with Web Development in 2025',
    excerpt: 'Learn the essential skills and tools needed to begin your web development journey in 2025.',
    image: 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    date: '2025-05-15',
    author: 'Moudondi Godfried',
    slug: 'getting-started-web-development-2025',
    keywords: ['web development', 'programming', 'beginners', 'HTML', 'CSS', 'JavaScript'],
    readTime: '8 min read',
    wordCount: 1200,
    content: `
      <p>Web development continues to evolve at a rapid pace. As we move through 2025, the landscape has shifted dramatically from just a few years ago. This guide will help newcomers navigate the essential skills and tools needed to begin a successful web development journey.</p>
      
      <h2>The Foundation: HTML, CSS, and JavaScript</h2>
      <p>Despite all the new frameworks and tools, the fundamentals remain unchanged. HTML structures your content, CSS styles it, and JavaScript makes it interactive. Mastering these three technologies is still the cornerstone of web development.</p>
      
      <p>Modern HTML5 semantic elements help create more accessible and SEO-friendly websites. CSS has evolved with features like Grid and Flexbox making complex layouts simpler. JavaScript has transformed with ES6+ features making code more readable and maintainable.</p>
      
      <img src="https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Code on a screen" class="my-8 rounded-lg shadow-md" />
      
      <h2>Frontend Frameworks and Libraries</h2>
      <p>React continues to dominate the frontend ecosystem, with Vue and Angular maintaining strong communities. These frameworks simplify building complex user interfaces and managing application state.</p>
      
      <p>Static site generators and meta-frameworks like Next.js, Nuxt, and Astro have gained significant traction for their performance benefits and developer experience.</p>
      
      <h2>The Backend Landscape</h2>
      <p>Node.js remains popular for JavaScript developers who want to use the same language across the stack. However, languages and frameworks like Python (with Django or FastAPI), Ruby (with Rails), and Go continue to have strong adoption.</p>
      
      <h2>Database Technologies</h2>
      <p>While SQL databases like PostgreSQL and MySQL remain foundational, NoSQL options like MongoDB and edge databases are gaining ground for specific use cases. GraphQL has become a standard for flexible API design, though REST APIs are still widely used.</p>
      
      <h2>Essential Developer Tools</h2>
      <p>Getting comfortable with version control (Git), package managers (npm/yarn/pnpm), and build tools (Vite, Webpack) is crucial. Additionally, understanding containerization with Docker and deployment pipelines will make your development process smoother.</p>
      
      <img src="https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Developer tools" class="my-8 rounded-lg shadow-md" />
      
      <h2>Conclusion</h2>
      <p>Web development in 2025 offers exciting opportunities with powerful tools and technologies. The key is to master the fundamentals first, then gradually expand your knowledge into more specialized areas based on your interests and career goals.</p>
      
      <p>Remember that the learning journey never truly ends in this field – continuous learning is part of what makes web development such a dynamic and rewarding career path.</p>
    `
  },
  {
    id: 2,
    title: 'The Future of AI in Web Design',
    excerpt: 'Explore how artificial intelligence is transforming the way we design and build websites.',
    image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    date: '2025-05-10',
    author: 'Moudondi Godfried',
    slug: 'future-of-ai-web-design',
    keywords: ['AI', 'web design', 'machine learning', 'automation', 'UX design'],
    readTime: '10 min read',
    wordCount: 1500,
    content: `
      <p>Artificial intelligence is revolutionizing countless industries, and web design is no exception. From automating repetitive tasks to generating entire layouts, AI tools are reshaping how developers and designers approach their work.</p>
      
      <h2>AI-Powered Design Assistance</h2>
      <p>Modern design tools now incorporate AI to suggest layout improvements, color schemes, and typography pairings. These suggestions are based on design principles and user behavior data, helping designers make more informed decisions.</p>
      
      <p>AI can analyze thousands of successful websites and extract patterns that lead to higher engagement and conversion rates. This knowledge is then applied to new designs to optimize user experience from the start.</p>
      
      <img src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="AI visualization" class="my-8 rounded-lg shadow-md" />
      
      <h2>Code Generation and Optimization</h2>
      <p>AI-powered code generators can now transform design mockups into production-ready HTML and CSS. While these tools aren't perfect, they significantly speed up the development process, especially for standard UI components.</p>
      
      <p>Performance optimization is another area where AI excels. Machine learning algorithms can identify performance bottlenecks and suggest improvements to make websites faster and more efficient.</p>
      
      <h2>Personalized User Experiences</h2>
      <p>Perhaps the most significant impact of AI on web design is the ability to create highly personalized user experiences. By analyzing user behavior, AI can dynamically adjust content, layout, and functionality to match individual preferences.</p>
      
      <p>This level of personalization was previously impossible at scale, but AI makes it feasible even for websites with millions of users.</p>
      
      <h2>Ethical Considerations</h2>
      <p>As with any AI application, ethical considerations are paramount. Issues around data privacy, algorithmic bias, and accessibility must be carefully addressed when implementing AI-powered web design solutions.</p>
      
      <img src="https://images.unsplash.com/photo-1531297484001-80022131f5a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Technology concept" class="my-8 rounded-lg shadow-md" />
      
      <h2>The Human Element</h2>
      <p>Despite these advancements, the human element of web design remains irreplaceable. AI tools are best viewed as collaborators that handle routine tasks and provide insights, allowing designers and developers to focus on creativity and problem-solving.</p>
      
      <p>The most successful web projects will likely combine human creativity with AI efficiency, leveraging the strengths of both to create exceptional digital experiences.</p>
      
      <h2>Conclusion</h2>
      <p>The future of web design is a collaboration between human creativity and artificial intelligence. By embracing AI tools thoughtfully, designers and developers can create more effective, personalized, and accessible websites while focusing their energy on the aspects of design that require human insight and creativity.</p>
    `
  },
  {
    id: 3,
    title: 'Responsive Design Best Practices',
    excerpt: 'A comprehensive guide to creating responsive websites that work flawlessly across all devices.',
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    date: '2025-05-05',
    author: 'Moudondi Godfried',
    slug: 'responsive-design-best-practices',
    keywords: ['responsive design', 'CSS', 'media queries', 'flexbox', 'grid'],
    readTime: '12 min read',
    wordCount: 1800,
    content: `
      <p>As the variety of devices used to access the web continues to expand, responsive design is more critical than ever. Creating websites that adapt seamlessly to different screen sizes ensures that all users have an optimal experience regardless of the device they're using.</p>
      
      <h2>Fluid Layouts and Flexible Grids</h2>
      <p>The foundation of responsive design is a fluid layout that can adapt to different screen sizes. Using relative units like percentages instead of fixed pixels allows content to resize proportionally to the viewport.</p>
      
      <p>Modern CSS grid and flexbox make it easier than ever to create flexible layouts that rearrange themselves based on available space. These powerful layout systems have largely replaced older techniques based on floats and positioning.</p>
      
      <img src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Responsive design" class="my-8 rounded-lg shadow-md" />
      
      <h2>Media Queries: Beyond Breakpoints</h2>
      <p>While breakpoints remain useful, modern responsive design goes beyond simply changing layouts at certain screen widths. Media queries can now target device capabilities, allowing us to optimize for factors like touch input, display quality, and even user preferences like reduced motion.</p>
      
      <p>The container query specification is also changing how we think about responsive design, allowing components to respond to their container's size rather than just the viewport.</p>
      
      <h2>Images and Media Optimization</h2>
      <p>Responsive images are crucial for performance. Using techniques like srcset, sizes, and picture elements allows browsers to download the most appropriate image size for the current display.</p>
      
      <p>Video and other rich media also need responsive approaches, with considerations for bandwidth and playback capabilities across devices.</p>
      
      <h2>Performance Considerations</h2>
      <p>Responsive design and performance are deeply intertwined. Mobile users often have slower connections and less powerful devices, making performance optimization particularly important.</p>
      
      <p>Techniques like lazy loading, code splitting, and serving different asset versions based on network conditions help ensure fast experiences across all devices.</p>
      
      <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Mobile responsive design" class="my-8 rounded-lg shadow-md" />
      
      <h2>Testing Across Devices</h2>
      <p>Thorough testing is essential for responsive design success. Using a combination of device emulators, real devices, and automated testing tools helps catch issues before they reach users.</p>
      
      <p>Browser developer tools have evolved to make responsive testing easier, with features for emulating different devices, network conditions, and user inputs.</p>
      
      <h2>Conclusion</h2>
      <p>Responsive design continues to evolve alongside web technologies and user expectations. By embracing fluid layouts, modern CSS techniques, optimized media, and thorough testing, developers can create websites that provide excellent experiences across the entire spectrum of devices used to access the web.</p>
      
      <p>The goal remains the same: a single website that adapts beautifully to provide the best possible experience for every user, regardless of how they access it.</p>
    `
  }
];

const BlogArticle = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const { getAlternateLanguages, getCanonicalUrl, getCurrentUrl, getImageUrl } = getDomainUtils();
  
  // Find the article with the matching ID
  const article = blogArticles.find(article => article.id === Number(id));
  
  // Scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle case when article is not found
  if (!article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="section flex-grow pt-32">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl font-bold mb-6 dark:text-white">{t('blogArticle.notFound', 'Article Not Found')}</h1>
              <p className="mb-8 dark:text-gray-300">{t('blogArticle.notFoundDesc', 'The article you are looking for does not exist or may have been removed.')}</p>
              <Link to="/blog" className="text-developer-accent dark:text-developer-accent-light font-medium flex items-center justify-center gap-2">
                <ChevronLeft className="h-4 w-4" />
                {t('blogArticle.backToBlog', 'Back to Blog')}
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Generate structured data for blog post */}
      {(() => {
        const structuredData = structuredDataGenerator.generateContextualSchema('blog', {
          title: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.date,
          dateModified: article.date,
          url: getCurrentUrl(`/blog/${article.id}`),
          keywords: article.keywords,
          wordCount: article.wordCount || 1200
        }, currentLang);

        const alternateLanguages = getAlternateLanguages(`/blog/${article.id}`);

        return (
          <SEOHead
            title={article.title}
            description={article.excerpt}
            ogImage={article.image}
            ogUrl={getCurrentUrl(`/blog/${article.id}`)}
            ogType="article"
            keywords={article.keywords.join(', ')}
            canonical={getCanonicalUrl(`/blog/${article.id}`)}
            lang={currentLang}
            alternateLanguages={alternateLanguages}
            structuredData={structuredData}
          />
        );
      })()}
      
      {/* Hero Header with Image */}
      <div className="w-full h-[50vh] md:h-[60vh] relative mt-16">
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        <OptimizedImage 
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover"
          priority
        />
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <div className="container-custom text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-4 max-w-4xl mx-auto">{article.title}</h1>
            <div className="text-gray-200 mb-6">
              <span>{new Date(article.date).toLocaleDateString()}</span>
              <span className="mx-2">•</span>
              <span>{article.author}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Article Content */}
      <div className="section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {/* Back to blog link */}
            <Link to="/blog" className="text-developer-accent dark:text-developer-accent-light font-medium flex items-center gap-2 mb-8">
              <ChevronLeft className="h-4 w-4" />
              {t('blogArticle.backToBlog', 'Back to Blog')}
            </Link>
            
            {/* Article content - using dangerouslySetInnerHTML for demo purposes
                In a real app, you'd want to use a proper markdown or rich text renderer */}
            <div 
              className="prose prose-lg max-w-none dark:prose-invert dark:text-gray-300 prose-headings:font-display prose-img:rounded-lg prose-img:shadow-md"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>
            
            {/* Author section */}
            <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold mb-2 dark:text-white">{t('blogArticle.aboutAuthor', 'About the Author')}</h3>
              <p className="text-developer-neutral dark:text-gray-300">
                {article.author} is a web developer and technical writer specializing in modern web technologies.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default BlogArticle;
